/**
 * AdminDashboard — 관리자 대시보드 (2026-10-14 워크스페이스 디자인, 레퍼런스 «Today»)
 *
 * 구조:
 *   [머리] 인사(큰 글씨) · 오늘 요약 한 문장(굵은 숫자) | 갱신 단추 · «이것부터 보기»(검정)
 *   [오늘 한눈에] 처리율 큰 숫자 + 숫자 다섯 + 바코드          (DashboardKpiStrip)
 *   [2열] 오늘의 할 일(펼친 한 건 + 목록)  |  팀 업무 지도 · 멘션 & 활동
 *
 * 데이터·동작은 그대로다(useActionQueue · 60초 자동 갱신 · 드릴다운 모달).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ChevronRight, RotateCw } from 'lucide-react';
import { Button } from '../../components/ui/shadcn/button';
import { useAuth } from '../../context/AuthContext';
import dashboardService from '../../services/dashboard';
import useActionQueue from './hooks/useActionQueue';
import DashboardKpiStrip from './components/DashboardKpiStrip';
import TodayActionQueue from './components/TodayActionQueue';
import ActivityFeedCard from './components/ActivityFeedCard';
import TeamMonitoringView from './monitoring/TeamMonitoringView';

function greeting(h: number): string {
  if (h >= 5 && h < 11) return '좋은 아침이에요';
  if (h >= 11 && h < 17) return '좋은 오후예요';
  if (h >= 17 && h < 22) return '좋은 저녁이에요';
  return '늦게까지 수고 많아요';
}

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

const AdminDashboard: React.FC = () => {
  const { state: authState } = useAuth();
  const userName = authState.user?.name || authState.user?.email?.split('@')[0] || '사용자';

  const {
    queue, loading, error, briefing, briefingLoading,
    unreadCount, refresh, retry, markDone, undo, snoozeToday,
  } = useActionQueue();

  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date>(new Date());

  const loadStats = useCallback(async () => {
    try {
      const stats = await dashboardService.getStats();
      setPendingApprovals(stats?.pending_approvals ?? 0);
    } catch {
      // 통계 실패는 조용히 — KPI 0 유지
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  // 60초 자동 새로고침 (탭 활성 시) — 피드/승인 대기. 큐는 훅 내부 20초 폴링+SSE 담당
  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden) {
        loadStats();
        setRefreshTick((t) => t + 1);
        setLastRefreshedAt(new Date());
      }
    }, 60_000);
    return () => clearInterval(interval);
  }, [loadStats]);

  const handleManualRefresh = useCallback(() => {
    refresh();
    loadStats();
    setRefreshTick((t) => t + 1);
    setLastRefreshedAt(new Date());
  }, [refresh, loadStats]);

  const total = queue?.total ?? 0;
  const sla = queue?.groups.find((g) => g.key === 'sla')?.count ?? 0;
  const customer = queue?.groups.find((g) => g.key === 'customer_request')?.count ?? 0;
  const timeStr = lastRefreshedAt.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="dash">
      {/* 머리 */}
      <header className="dash-head">
        <div style={{ minWidth: 0 }}>
          <h1 className="ws-h1">{greeting(new Date().getHours())}, {userName}님</h1>
          <p className="ws-lede">
            {queue == null ? '할 일을 모으는 중입니다…' : total === 0 ? (
              <>처리할 일이 모두 끝났습니다. 새 요청이 오면 여기에 바로 뜹니다.</>
            ) : (
              <>
                남은 할 일 {total.toLocaleString()}건.{' '}
                {sla > 0 ? <strong>{sla}건이 마감을 지났거나 임박했고</strong> : <>마감이 지난 일은 없고</>}
                {customer > 0 ? <>, 고객 요청이 <strong>{customer}건</strong> 기다리고 있습니다.</> : <>, 고객 요청은 모두 처리했습니다.</>}
              </>
            )}
          </p>
        </div>
        <div className="dash-head-actions">
          <Button type="button" variant="ghost" className="ws-btn" onClick={handleManualRefresh} title="새로고침">
            <RotateCw size={15} aria-hidden />
            {timeStr} 갱신
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="ws-btn ws-btn--primary"
            onClick={() => scrollToId(sla > 0 ? 'aq-group-sla' : 'today-action-queue')}
          >
            {sla > 0 ? '마감부터 보기' : '할 일 보기'}
            <ChevronRight size={16} aria-hidden />
          </Button>
        </div>
      </header>

      {/* 오늘 한눈에 */}
      <DashboardKpiStrip queue={queue} pendingApprovals={pendingApprovals} unreadCount={unreadCount} />

      {/* 2열: 할 일 | 팀 지도 · 활동 */}
      <div className="dash-grid">
        <TodayActionQueue
          queue={queue}
          loading={loading}
          error={error}
          briefing={briefing}
          briefingLoading={briefingLoading}
          onRetry={retry}
          onDone={markDone}
          onUndo={undo}
          onSnooze={snoozeToday}
        />
        <div className="dash-side">
          <TeamMonitoringView />
          <ActivityFeedCard refreshTick={refreshTick} />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
